import {
  type EnvironmentId,
  type ProjectCreator,
  type ProjectId,
  type ResolvedKeybindingsConfig,
  type ThreadId,
} from "@t3tools/contracts";
import { memo } from "react";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { SidebarTrigger } from "../ui/sidebar";
import { SubAgentsHeaderButton } from "./SubAgentsHeaderButton";
import { DocumentsHeaderButton } from "./DocumentsHeaderButton";
import { DraftProjectChip } from "./DraftProjectChip";
import { ProjectOwnerAvatar, TitleAuthorTooltip } from "../ProjectOwnerAvatar";
import type { Project } from "../../types";

export interface ChatHeaderDraftProjectPicker {
  projects: ReadonlyArray<Project>;
  activeProject: Project | undefined;
  /** The draft points at a project that no longer exists (as opposed to none chosen yet). */
  projectMissing: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectProject: (project: Project) => void;
}

interface ChatHeaderProps {
  activeThreadEnvironmentId: EnvironmentId;
  activeThreadId: ThreadId;
  activeThreadTitle: string;
  activeProjectName: string | undefined;
  /** Shown on a team server alone; `null` elsewhere and for a project with no recorded owner. */
  activeProjectOwner: ProjectCreator | null;
  draftProjectPicker: ChatHeaderDraftProjectPicker | null;
  projectId: ProjectId | null;
  projectCwd: string | null;
  hasMessages: boolean;
  keybindings: ResolvedKeybindingsConfig;
}

export const ChatHeader = memo(function ChatHeader({
  activeThreadEnvironmentId,
  activeThreadId,
  activeThreadTitle,
  activeProjectName,
  activeProjectOwner,
  draftProjectPicker,
  projectId,
  projectCwd,
  hasMessages,
  keybindings,
}: ChatHeaderProps) {
  return (
    <div className="@container/header-actions flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-wrap items-center gap-2 overflow-hidden sm:flex-1 sm:flex-nowrap sm:gap-3">
        <SidebarTrigger className="size-7 shrink-0 md:hidden" />
        <Tooltip>
          <TooltipTrigger
            render={
              <h2
                aria-label={activeThreadTitle}
                className="min-w-0 shrink truncate text-sm font-medium text-foreground"
              >
                {activeThreadTitle}
              </h2>
            }
          />
          <TooltipPopup side="top">{activeThreadTitle}</TooltipPopup>
        </Tooltip>
        {draftProjectPicker ? (
          <DraftProjectChip
            projects={draftProjectPicker.projects}
            activeProject={draftProjectPicker.activeProject}
            activeProjectOwner={activeProjectOwner}
            projectMissing={draftProjectPicker.projectMissing}
            keybindings={keybindings}
            open={draftProjectPicker.open}
            onOpenChange={draftProjectPicker.onOpenChange}
            onSelectProject={draftProjectPicker.onSelectProject}
          />
        ) : activeProjectName ? (
          <TitleAuthorTooltip
            title={activeProjectName}
            author={activeProjectOwner}
            side="bottom"
            render={
              <Badge
                variant="outline"
                className="min-w-0 max-w-full shrink overflow-hidden sm:max-w-56"
              >
                {activeProjectOwner ? (
                  <ProjectOwnerAvatar owner={activeProjectOwner} size="sm" />
                ) : null}
                <span className="min-w-0 truncate">{activeProjectName}</span>
              </Badge>
            }
          />
        ) : null}
      </div>
      <div className="flex min-w-0 flex-wrap items-center justify-start gap-2 sm:shrink-0 sm:justify-end @3xl/header-actions:gap-3">
        {hasMessages && (
          <SubAgentsHeaderButton
            environmentId={activeThreadEnvironmentId}
            threadId={activeThreadId}
          />
        )}
        {hasMessages && projectId !== null && projectCwd !== null && (
          <DocumentsHeaderButton
            environmentId={activeThreadEnvironmentId}
            projectId={projectId}
            projectCwd={projectCwd}
          />
        )}
      </div>
    </div>
  );
});
